import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@pantry/ui';
import {
  type ItemUnit,
  getConversionReferences,
  getUnitCategory,
  UNIT_DISPLAY_LABELS,
} from '@pantry/api-client';
import { useLingui } from '@lingui/react/macro';

interface UnitConversionTooltipProps {
  /** The unit to show conversions for */
  unit: ItemUnit;
  /** Size variant matching stepper buttons */
  size?: 'sm' | 'default';
  /** Optional className for the trigger button */
  className?: string;
}

/**
 * Info icon with tooltip showing common unit conversions.
 * Supports hybrid hover/click behavior:
 * - Hover: Opens temporarily, closes on mouse leave
 * - Click: Toggles pinned state - stays open until clicked again
 * Hidden for discrete 'item' unit type (no conversions applicable).
 */
export function UnitConversionTooltip({
  unit,
  size = 'default',
  className,
}: UnitConversionTooltipProps) {
  const { t } = useLingui();
  const [isPinned, setIsPinned] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Don't show for discrete items - no conversions applicable
  const category = getUnitCategory(unit);
  if (category === 'discrete') {
    return null;
  }

  const references = getConversionReferences(unit);

  if (references.length === 0) {
    return null;
  }

  // Size classes to match stepper buttons
  const sizeClasses = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';
  const iconClasses = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';

  const handleMouseEnter = () => {
    if (!isPinned) setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (!isPinned) setIsOpen(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPinned) {
      // Clicking again unpins and closes
      setIsPinned(false);
      setIsOpen(false);
    } else {
      // Pin it open
      setIsPinned(true);
      setIsOpen(true);
    }
  };

  // Handle Radix's onOpenChange - only allow it to control state when not pinned
  const handleOpenChange = (open: boolean) => {
    if (!isPinned) {
      setIsOpen(open);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip open={isOpen} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors ${sizeClasses} ${isPinned ? 'border-primary bg-primary/5' : ''} ${className ?? ''}`}
            aria-label={t`Conversion references for ${UNIT_DISPLAY_LABELS[unit]}`}
            aria-pressed={isPinned}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <InformationCircleIcon className={iconClasses} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-popover text-popover-foreground border shadow-md max-w-xs"
        >
          <div className="space-y-1">
            <p className="font-medium text-xs mb-1.5">{t`${UNIT_DISPLAY_LABELS[unit]} conversions`}</p>
            {references.map((ref, index) => (
              <p key={index} className="text-xs text-muted-foreground">
                {ref.display}
              </p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
